import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  computed,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { AppointmentApi, DashboardService } from '../dashboard.service';
import { AdministrationService } from '../administration.service';
import { Appointment } from '../interfaces/dashboard.component';

@Component({
  selector: 'app-dashboard-upcoming-appointments',
  standalone: false,
  templateUrl: './dashboard-upcoming-appointments.html',
  styleUrl: './dashboard-upcoming-appointments.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardUpcomingAppointmentsComponent implements OnInit {
  @Input() isEnabled = false;
  @Input() loading = false;

  @Output() toggleAccess = new EventEmitter<boolean>();

  // Raw rows from the API; the display models are derived so the weekday/day
  // labels re-localise when the interface language changes.
  private readonly rawAppointments = signal<AppointmentApi[]>([]);
  // Bumped on every language change so the derived labels recompute.
  private readonly lang = signal('');
  readonly appointments = computed<Appointment[]>(() => {
    this.lang();
    return this.mapAppointments(this.rawAppointments());
  });
  readonly loadingAppts = signal(true);

  constructor(
    private dashboardService: DashboardService,
    private administrationService: AdministrationService,
    private translate: TranslateService
  ) {
    this.translate.onLangChange.pipe(takeUntilDestroyed()).subscribe((e) => this.lang.set(e.lang));
  }

  // Opens the appointments calendar in a new tab — same destination as the
  // sidebar's Appointments button, but always a new tab (even for secretaries,
  // who redirect() would otherwise navigate in-place).
  openCalendar(): void {
    this.administrationService.getUrl('appointments').subscribe((url) => {
      window.open(url, '_blank', 'noopener');
    });
  }

  ngOnInit(): void {
    this.dashboardService.getAppointments().subscribe({
      next: (data) => {
        this.rawAppointments.set(data);
        this.loadingAppts.set(false);
      },
      error: () => this.loadingAppts.set(false),
    });
  }

  private mapAppointments(data: AppointmentApi[]): Appointment[] {
    const locale = this.translate.currentLang || 'en-US';
    return data.map((a) => {
      const dt = new Date(a.ExamDate);
      return {
        // Weekday + day number in the active language (e.g. Hebrew "יום א׳").
        day: dt.toLocaleDateString(locale, { weekday: 'short' }),
        date: dt.toLocaleDateString(locale, { day: 'numeric' }),
        name: `${a.First} ${a.Last}`,
        type: a.ExamCode ?? '',
        // 24-hour clock for every language (no AM/PM).
        time: dt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false }),
      };
    });
  }
}
